import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowDown, ArrowUp, ChevronRight, ImagePlus, Loader2, Plus, Save, Trash2 } from 'lucide-react'
import { useDirty } from '@/hooks/useDirty'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { FormField } from '@/components/FormField'
import { Combobox } from '@/components/Combobox'
import { CountryCombobox } from '@/components/CountryCombobox'
import { CurrencyCombobox } from '@/components/CurrencyCombobox'
import { Checkbox } from '@/components/ui/checkbox'
import { Toolbar } from '@/components/Toolbar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Modal } from '@/components/Modal'
import { useToast } from '@/hooks/useToast'
import {
  useCreateInvoice, useUpdateInvoice, useInvoice, useCustomers, useNextInvoiceNumber,
  useStripeConnectStatus,
} from '@/features/invoicing/queries'
import { usePayoutAccount } from '@/features/invoicing/payments/queries'
import { useCoa } from '@/features/coa/queries'
import { useTenantBankAccounts, useTenantSettings } from '@/features/company/queries'
import type { TenantBankAccount } from '@/features/company/types'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import { invoicingApi } from '@/features/invoicing/api'

type PaymentMethod = 'connect_express' | 'bokapsys_managed' | null

function formatBankDetails(b: TenantBankAccount): string {
  const lines: string[] = []
  if (b.bank_name) lines.push(b.bank_name)
  if (b.account_name) lines.push(`Account Name: ${b.account_name}`)
  if (b.account_number) lines.push(`Account Number: ${b.account_number}`)
  if (b.sort_code) lines.push(`Sort Code: ${b.sort_code}`)
  if (b.routing_number) lines.push(`Routing: ${b.routing_number}`)
  if (b.iban) lines.push(`IBAN: ${b.iban}`)
  if (b.swift) lines.push(`SWIFT: ${b.swift}`)
  return lines.join('\n')
}

type LineDraft = {
  description: string
  account_code: string
  quantity: string
  unit_price: string
  tax_rate: string
}

function blankLine(): LineDraft {
  return { description: '', account_code: '', quantity: '1', unit_price: '0', tax_rate: '0' }
}

function addDays(d: Date, days: number) {
  const r = new Date(d)
  r.setDate(r.getDate() + days)
  return r.toISOString().slice(0, 10)
}

function swap<T>(arr: T[], a: number, b: number): T[] {
  if (a < 0 || b < 0 || a >= arr.length || b >= arr.length) return arr
  const next = [...arr]
  ;[next[a], next[b]] = [next[b], next[a]]
  return next
}

export function InvoiceEditorPage() {
  const params = useParams<{ id?: string }>()
  const isEdit = !!params.id
  const invoiceId = isEdit ? parseInt(params.id as string, 10) : undefined
  const navigate = useNavigate()
  const { toast } = useToast()

  const existing = useInvoice(invoiceId)
  const { data: customersData } = useCustomers({ isActive: true })
  const { data: coaData } = useCoa()
  const { data: nextNumData } = useNextInvoiceNumber()
  const { data: tenantSettings } = useTenantSettings()
  const { data: bankAccountsData } = useTenantBankAccounts()
  const { data: stripeConnect } = useStripeConnectStatus()
  const { data: payoutAccountData } = usePayoutAccount()
  const createMut = useCreateInvoice()
  const updateMut = useUpdateInvoice()

  const [customerId, setCustomerId] = useState<number | null>(null)
  const [issueDate, setIssueDate] = useState<string>(new Date().toISOString().slice(0, 10))
  const [dueDate, setDueDate] = useState<string>(addDays(new Date(), 30))
  const [currency, setCurrency] = useState<string>(
    () => (tenantSettings?.tenant?.currency || 'USD').toUpperCase(),
  )
  const [notes, setNotes] = useState('')
  const [terms, setTerms] = useState('')
  const [bankDetails, setBankDetails] = useState('')
  const [discount, setDiscount] = useState('0')
  // Shipping (migration 080): optional flat charge added after discount, posted
  // to the user-picked income account so it shows separately on Income Statement.
  const [shippingAmount, setShippingAmount] = useState('0')
  const [shippingAccountCode, setShippingAccountCode] = useState<string>('')
  // Header tax rate (migration 080): when set, replaces per-line tax with a
  // single % applied to subtotal (e.g. 20 → UK VAT 20%). Empty/null means
  // "use per-line tax_rate" (the existing behaviour).
  const [headerTaxRatePct, setHeaderTaxRatePct] = useState<string>('')
  const [useHeaderTax, setUseHeaderTax] = useState(false)
  const [lines, setLines] = useState<LineDraft[]>([blankLine()])

  // PO number + branding override fields. Sender_* fall back to the tenant
  // defaults when empty (backend resolves via COALESCE). The logo key is
  // stored as an S3 key (not a full URL) so the PDF + hosted page signing
  // logic works identically for both tenant + per-invoice logos.
  const [poNumber, setPoNumber] = useState<string>('')
  const [poAutoFilled, setPoAutoFilled] = useState(false)
  const [senderBusinessName, setSenderBusinessName] = useState('')
  const [senderBusinessPhone, setSenderBusinessPhone] = useState('')
  const [senderBusinessEmail, setSenderBusinessEmail] = useState('')
  const [senderAddressLine1, setSenderAddressLine1] = useState('')
  const [senderAddressLine2, setSenderAddressLine2] = useState('')
  const [senderCity, setSenderCity] = useState('')
  const [senderCountry, setSenderCountry] = useState('')
  const [senderLogoKey, setSenderLogoKey] = useState<string | null>(null)
  const [senderLogoPreview, setSenderLogoPreview] = useState<string | null>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)

  // Per-field branding visibility toggles. Default all on; unchecking hides
  // the field on the PDF even if a tenant default would otherwise render.
  const [showLogo, setShowLogo] = useState(true)
  const [showBusinessName, setShowBusinessName] = useState(true)
  const [showBusinessPhone, setShowBusinessPhone] = useState(true)
  const [showBusinessEmail, setShowBusinessEmail] = useState(true)
  const [showBusinessAddress, setShowBusinessAddress] = useState(true)
  const [showBankDetails, setShowBankDetails] = useState(true)

  // Bank-account picker (sourced from Company bank accounts). When the user
  // picks an account we fill `bankDetails` (freetext) with a formatted copy
  // so the existing PDF + textarea flow stays unchanged.
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null)

  // Per-invoice payment-collection method. `undefined` means "inherit from
  // tenant default" (the common case). The user can override per-invoice
  // to `null` (bank-transfer only, hides Pay button on the hosted page) or
  // pin a specific method even if their tenant default later changes.
  const [paymentMethodOverride, setPaymentMethodOverride] =
    useState<PaymentMethod | undefined>(undefined)

  // Validation gate when neither a ready online method nor bank details
  // are available. The modal points the user to the place that fixes it.
  const [gateOpen, setGateOpen] = useState(false)
  const [gateReason, setGateReason] = useState<{
    title: string
    body: string
    cta?: { label: string; href: string }
  } | null>(null)

  // Branding section is collapsed by default - most users send invoices that
  // inherit Company-settings branding without per-invoice tweaks. Click the
  // header to reveal the override fields.
  const [brandingOpen, setBrandingOpen] = useState(false)

  useEffect(() => {
    if (!isEdit || !existing.data?.invoice) return
    const inv = existing.data.invoice
    setCustomerId(inv.customer_id)
    setIssueDate(inv.issue_date)
    setDueDate(inv.due_date)
    setCurrency(inv.currency)
    setNotes(inv.notes || '')
    setTerms(inv.terms || '')
    setBankDetails(inv.bank_details || '')
    setDiscount(String(inv.discount || 0))
    setShippingAmount(String(inv.shipping_amount || 0))
    setShippingAccountCode(inv.shipping_account_code || '')
    if (inv.header_tax_rate_pct != null) {
      setUseHeaderTax(true)
      setHeaderTaxRatePct(String(inv.header_tax_rate_pct))
    } else {
      setUseHeaderTax(false)
      setHeaderTaxRatePct('')
    }
    setPoNumber(inv.po_number || '')
    setPoAutoFilled(false)
    setSenderBusinessName(inv.sender_business_name || '')
    setSenderBusinessPhone(inv.sender_business_phone || '')
    setSenderBusinessEmail(inv.sender_business_email || '')
    setSenderAddressLine1(inv.sender_address_line1 || '')
    setSenderAddressLine2(inv.sender_address_line2 || '')
    setSenderCity(inv.sender_city || '')
    setSenderCountry(inv.sender_country || '')
    setSenderLogoKey(inv.sender_logo_url || null)
    setSenderLogoPreview(null)
    setShowLogo(inv.show_logo !== false)
    setShowBusinessName(inv.show_business_name !== false)
    setShowBusinessPhone(inv.show_business_phone !== false)
    setShowBusinessEmail(inv.show_business_email !== false)
    setShowBusinessAddress(inv.show_business_address !== false)
    setShowBankDetails(inv.show_bank_details !== false)
    // On edit hydrate the per-invoice method override exactly as stored
    // (including null, which means "bank-transfer only"). Treat the
    // hydrated value as an explicit override so we don't silently drift
    // back to the tenant default.
    setPaymentMethodOverride(inv.payment_collection_method ?? null)
    setLines(inv.lines.map(l => ({
      description: l.description,
      account_code: l.account_code,
      quantity: String(l.quantity),
      unit_price: String(l.unit_price),
      tax_rate: String(l.tax_rate),
    })))
  }, [isEdit, existing.data])

  // On create: when tenant settings first resolve, snap the currency to the
  // tenant default (regardless of whether the initial state was already USD,
  // GBP, or anything else). After that one sync the user's pick is sticky.
  // Edit path leaves the invoice's stored currency alone.
  const tenantCurrencySynced = useRef(false)
  useEffect(() => {
    if (isEdit) return
    if (tenantCurrencySynced.current) return
    const tenantCurrency = tenantSettings?.tenant?.currency
    if (tenantCurrency) {
      setCurrency(tenantCurrency.toUpperCase())
      tenantCurrencySynced.current = true
    }
  }, [isEdit, tenantSettings?.tenant?.currency])

  // On NEW invoice: auto-select the tenant's default bank account and
  // populate bank details. Tenant address/phone/email/name are left blank
  // on the override fields so the PDF falls through to the tenant defaults.
  const bankAccounts = useMemo(() => bankAccountsData?.bank_accounts ?? [], [bankAccountsData])
  useEffect(() => {
    if (isEdit) return
    if (selectedBankId !== null) return
    if (bankAccounts.length === 0) return
    const def = bankAccounts.find(b => b.is_default) ?? bankAccounts[0]
    setSelectedBankId(def.id)
    if (!bankDetails.trim()) setBankDetails(formatBankDetails(def))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, bankAccounts])

  function onPickBank(id: number | null) {
    setSelectedBankId(id)
    if (id === null) {
      setBankDetails('')
      return
    }
    const acc = bankAccounts.find(b => b.id === id)
    if (acc) setBankDetails(formatBankDetails(acc))
  }

  // ────────────────────────────────────────────────────────────────────
  // Payment-collection readiness derived state
  //
  // The per-invoice "method" is the override if the user chose one,
  // otherwise the tenant default from /stripe-connect/status. `null`
  // means "this invoice is bank-transfer only" - the hosted /pay page
  // hides the Pay button when the invoice's snapshot is null.
  //
  // Readiness rules mirror the backend `resolveAndValidateInvoicePaymentPath`:
  //   - connect_express  → tenant must have charges_enabled = TRUE
  //   - bokapsys_managed → tenant must have a payout-destination bank account
  //   - null             → no online check; the bank-details fallback must exist
  // ────────────────────────────────────────────────────────────────────
  const tenantDefaultMethod: PaymentMethod = (stripeConnect?.mode ?? null) as PaymentMethod
  const effectiveMethod: PaymentMethod = paymentMethodOverride === undefined
    ? tenantDefaultMethod
    : paymentMethodOverride

  const stripeReady = !!(stripeConnect?.stripeAccountId && stripeConnect?.chargesEnabled)
  const payoutReady = !!payoutAccountData?.account
  const methodReady =
    effectiveMethod === 'connect_express' ? stripeReady
    : effectiveMethod === 'bokapsys_managed' ? payoutReady
    : true // null is always "ready" - no online path needed
  const bankFallbackReady = showBankDetails && bankDetails.trim().length > 0
  const hasPayablePath = effectiveMethod !== null
    ? methodReady
    : bankFallbackReady

  // On create, auto-fetch the next PO number for the selected customer. The
  // user can still type a custom PO; we only overwrite when the current
  // value is empty or was itself auto-filled from a previous customer.
  useEffect(() => {
    if (isEdit || !customerId) return
    let cancelled = false
    invoicingApi.nextPoNumber(customerId).then((res) => {
      if (cancelled) return
      const next = res.po_number || ''
      setPoNumber((cur) => (!cur || poAutoFilled ? next : cur))
      setPoAutoFilled(true)
    }).catch(() => { /* non-critical */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, isEdit])

  const customerOptions = useMemo(() => (customersData?.customers || []).map(c => ({
    value: c.id,
    label: c.display_name,
    description: c.email || undefined,
  })), [customersData])

  // Revenue account options (Income Statement accounts from the tenant's COA).
  const revenueAccountOptions = useMemo(() => {
    const list = Array.isArray(coaData) ? coaData : []
    return list
      .filter(row => row.account_code && row.statement_type === 'Income Statement')
      .map(row => ({
        value: row.account_code,
        label: `${row.account_code} · ${row.subcategory_name}`,
        description: row.main_category,
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
  }, [coaData])

  const totals = useMemo(() => {
    let subtotal = 0, perLineTax = 0
    for (const l of lines) {
      const q = Number(l.quantity || 0)
      const up = Number(l.unit_price || 0)
      const tr = Number(l.tax_rate || 0)
      const amt = +(q * up).toFixed(2)
      subtotal += amt
      // tax_rate is a percentage - a 10 in the field means 10%. Divide by 100.
      perLineTax += +(amt * (tr / 100)).toFixed(2)
    }
    subtotal = +subtotal.toFixed(2)
    // Header tax replaces per-line tax when toggled on (mirrors the
    // computeTotals() branch on the backend).
    const headerPct = useHeaderTax && headerTaxRatePct !== ''
      ? Math.max(0, Number(headerTaxRatePct) || 0)
      : null
    const tax = headerPct != null
      ? +(subtotal * (headerPct / 100)).toFixed(2)
      : +perLineTax.toFixed(2)
    const d = Math.max(0, +Number(discount || 0).toFixed(2))
    const ship = Math.max(0, +Number(shippingAmount || 0).toFixed(2))
    const total = Math.max(0, +(subtotal + tax - d + ship).toFixed(2))
    return { subtotal, tax, discount: d, shipping: ship, total }
  }, [lines, discount, shippingAmount, useHeaderTax, headerTaxRatePct])

  const tenantLogoUrl = tenantSettings?.tenant?.logo_url || null
  const displayedLogo = senderLogoPreview || (senderLogoKey ? null : tenantLogoUrl)

  async function onLogoFile(file: File | undefined) {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please choose an image file', variant: 'destructive' })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Image must be 5 MB or smaller', variant: 'destructive' })
      return
    }
    setUploadingLogo(true)
    try {
      const key = await invoicingApi.uploadInvoiceLogo(file)
      setSenderLogoKey(key)
      // Optimistic preview so the user sees the upload took. The signed
      // URL only exists after the backend renders it (PDF + hosted page).
      const reader = new FileReader()
      reader.onload = (e) => setSenderLogoPreview(String(e.target?.result ?? ''))
      reader.readAsDataURL(file)
      toast({ title: 'Logo attached to this invoice' })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Logo upload failed', variant: 'destructive' })
    } finally {
      setUploadingLogo(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  function clearLogoOverride() {
    setSenderLogoKey(null)
    setSenderLogoPreview(null)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  function updateLine(idx: number, patch: Partial<LineDraft>) {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l))
  }

  function removeLine(idx: number) {
    setLines(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx))
  }

  function moveLine(idx: number, direction: -1 | 1) {
    setLines(prev => swap(prev, idx, idx + direction))
  }

  // Dirty-check the editor. The snapshot mirrors the payload sent to
  // create/update so any field the user can change unlocks Save and
  // transient UI state (modals, upload progress, branding-section
  // collapse) is intentionally excluded.
  const formSnapshot = useMemo(
    () => ({
      customerId,
      issueDate,
      dueDate,
      currency,
      notes,
      terms,
      bankDetails,
      discount,
      shippingAmount,
      shippingAccountCode,
      headerTaxRatePct: useHeaderTax ? headerTaxRatePct : '',
      poNumber,
      senderBusinessName,
      senderBusinessPhone,
      senderBusinessEmail,
      senderAddressLine1,
      senderAddressLine2,
      senderCity,
      senderCountry,
      senderLogoKey,
      showLogo,
      showBusinessName,
      showBusinessPhone,
      showBusinessEmail,
      showBusinessAddress,
      showBankDetails,
      paymentMethodOverride: paymentMethodOverride ?? null,
      lines,
    }),
    [
      customerId, issueDate, dueDate, currency, notes, terms, bankDetails,
      discount, shippingAmount, shippingAccountCode, useHeaderTax,
      headerTaxRatePct, poNumber, senderBusinessName, senderBusinessPhone,
      senderBusinessEmail, senderAddressLine1, senderAddressLine2, senderCity,
      senderCountry, senderLogoKey, showLogo, showBusinessName, showBusinessPhone,
      showBusinessEmail, showBusinessAddress, showBankDetails,
      paymentMethodOverride, lines,
    ],
  )
  const baselineSnapshot = useMemo(() => {
    if (!isEdit) {
      // New invoice: anything typed/added beyond the bare initial state
      // counts as dirty. The blank baseline mirrors the useState initialisers.
      return {
        customerId: null,
        issueDate: '',
        dueDate: '',
        currency: '',
        notes: '',
        terms: '',
        bankDetails: '',
        discount: '0',
        shippingAmount: '0',
        shippingAccountCode: '',
        headerTaxRatePct: '',
        poNumber: '',
        senderBusinessName: '',
        senderBusinessPhone: '',
        senderBusinessEmail: '',
        senderAddressLine1: '',
        senderAddressLine2: '',
        senderCity: '',
        senderCountry: '',
        senderLogoKey: null as string | null,
        showLogo: true,
        showBusinessName: true,
        showBusinessPhone: true,
        showBusinessEmail: true,
        showBusinessAddress: true,
        showBankDetails: true,
        paymentMethodOverride: null as PaymentMethod,
        lines: [blankLine()],
      }
    }
    const inv = existing.data?.invoice
    if (!inv) return null
    return {
      customerId: inv.customer_id,
      issueDate: inv.issue_date,
      dueDate: inv.due_date,
      currency: inv.currency,
      notes: inv.notes || '',
      terms: inv.terms || '',
      bankDetails: inv.bank_details || '',
      discount: String(inv.discount || 0),
      shippingAmount: String(inv.shipping_amount || 0),
      shippingAccountCode: inv.shipping_account_code || '',
      headerTaxRatePct: inv.header_tax_rate_pct != null ? String(inv.header_tax_rate_pct) : '',
      poNumber: inv.po_number || '',
      senderBusinessName: inv.sender_business_name || '',
      senderBusinessPhone: inv.sender_business_phone || '',
      senderBusinessEmail: inv.sender_business_email || '',
      senderAddressLine1: inv.sender_address_line1 || '',
      senderAddressLine2: inv.sender_address_line2 || '',
      senderCity: inv.sender_city || '',
      senderCountry: inv.sender_country || '',
      senderLogoKey: inv.sender_logo_url || null,
      showLogo: inv.show_logo !== false,
      showBusinessName: inv.show_business_name !== false,
      showBusinessPhone: inv.show_business_phone !== false,
      showBusinessEmail: inv.show_business_email !== false,
      showBusinessAddress: inv.show_business_address !== false,
      showBankDetails: inv.show_bank_details !== false,
      paymentMethodOverride: (inv.payment_collection_method ?? null) as PaymentMethod,
      lines: inv.lines.map((l) => ({
        description: l.description,
        account_code: l.account_code,
        quantity: String(l.quantity),
        unit_price: String(l.unit_price),
        tax_rate: String(l.tax_rate),
      })),
    }
  }, [isEdit, existing.data])
  const dirty = useDirty(formSnapshot, baselineSnapshot)

  async function onSave() {
    if (!customerId) { toast({ title: 'Select a customer', variant: 'destructive' }); return }
    if (lines.some(l => !l.description || !l.account_code || !(Number(l.quantity) > 0))) {
      toast({ title: 'Every line needs a description, account, and quantity > 0', variant: 'destructive' }); return
    }
    if (Number(shippingAmount || 0) > 0 && !shippingAccountCode) {
      toast({ title: 'Pick a revenue account for shipping (or set shipping to 0)', variant: 'destructive' }); return
    }
    const headerTaxValue = useHeaderTax && headerTaxRatePct !== ''
      ? Number(headerTaxRatePct)
      : null
    if (headerTaxValue != null && (!Number.isFinite(headerTaxValue) || headerTaxValue < 0)) {
      toast({ title: 'Header tax rate must be a non-negative number', variant: 'destructive' }); return
    }

    // Payment-path gate. Blocks save when no online method is ready AND
    // no bank-details fallback is set. The modal points the user to the
    // place that fixes their specific situation.
    if (!hasPayablePath) {
      if (effectiveMethod === 'connect_express') {
        setGateReason({
          title: 'Finish Stripe Connect setup',
          body: 'You picked Stripe Connect (Express) as your payment method, but onboarding is not complete. Complete it in Company → Payments, or switch this invoice to bank-transfer only by adding bank details below.',
          cta: { label: 'Open Company → Payments', href: '/company?tab=payments' },
        })
      } else if (effectiveMethod === 'bokapsys_managed') {
        setGateReason({
          title: 'Add a payout bank account',
          body: 'You picked Bokapsys-managed as your payment method, but no payout bank account is on file. Bokapsys uses it to pay you. Add it in Company → Bank Accounts, or switch this invoice to bank-transfer only by adding bank details below.',
          cta: { label: 'Open Company → Bank Accounts', href: '/company?tab=bank-accounts' },
        })
      } else {
        setGateReason({
          title: 'Pick a payment method or add bank details',
          body: 'This invoice has no way for the customer to pay. Pick Stripe Connect (Express) or Bokapsys-managed in Company → Payments, or add bank details below so the customer can pay by bank transfer.',
          cta: { label: 'Open Company → Payments', href: '/company?tab=payments' },
        })
      }
      setGateOpen(true)
      return
    }
    const payload = {
      customer_id: customerId,
      issue_date: issueDate,
      due_date: dueDate,
      currency,
      notes: notes || null,
      terms: terms || null,
      bank_details: showBankDetails && bankDetails ? bankDetails : null,
      discount: Number(discount || 0),
      shipping_amount: Number(shippingAmount || 0),
      shipping_account_code: Number(shippingAmount || 0) > 0 ? shippingAccountCode : null,
      header_tax_rate_pct: headerTaxValue,
      po_number: poNumber ? poNumber : null,
      sender_logo_url: senderLogoKey || null,
      sender_business_name: senderBusinessName || null,
      sender_business_phone: senderBusinessPhone || null,
      sender_business_email: senderBusinessEmail || null,
      sender_address_line1: senderAddressLine1 || null,
      sender_address_line2: senderAddressLine2 || null,
      sender_city: senderCity || null,
      sender_country: senderCountry || null,
      show_logo: showLogo,
      show_business_name: showBusinessName,
      show_business_phone: showBusinessPhone,
      show_business_email: showBusinessEmail,
      show_business_address: showBusinessAddress,
      show_bank_details: showBankDetails,
      // Send the per-invoice override (`null` is intentional to mean
      // "bank transfer only"). When the user hasn't touched the override
      // we send the resolved effective method anyway so the snapshot is
      // explicit on the row instead of relying on the backend re-reading
      // the tenant default at create time.
      payment_collection_method: effectiveMethod,
      lines: lines.map(l => ({
        description: l.description,
        account_code: l.account_code,
        quantity: Number(l.quantity),
        unit_price: Number(l.unit_price),
        tax_rate: Number(l.tax_rate || 0),
      })),
    }
    try {
      if (isEdit && invoiceId) {
        await updateMut.mutateAsync({ id: invoiceId, body: payload })
        toast({ title: 'Invoice updated' })
        navigate(`/invoicing/invoices/${invoiceId}`)
      } else {
        const res = await createMut.mutateAsync(payload)
        toast({ title: `Invoice ${res.invoice.invoice_number} created` })
        navigate(`/invoicing/invoices/${res.invoice.id}`)
      }
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Failed to save', variant: 'destructive' })
    }
  }

  const previewNumber = isEdit ? existing.data?.invoice?.invoice_number : nextNumData?.invoice_number

  return (
    <Page
      title={isEdit ? `Edit ${previewNumber || 'invoice'}` : 'New invoice'}
      description={isEdit ? 'Only drafts can be edited. Posted invoices must be deleted to change.' : `Will be saved as ${previewNumber || 'INV-0001'}.`}
      actions={
        <Toolbar align="end">
          <Button variant="secondary" onClick={() => navigate('/invoicing/invoices')}>Cancel</Button>
          <Button onClick={onSave} disabled={!dirty || createMut.isPending || updateMut.isPending}>
            <Save className="mr-2 size-4" /> {isEdit ? 'Save changes' : 'Save draft'}
          </Button>
        </Toolbar>
      }
    >
      <Card>
        <div className="grid gap-4 p-4 md:grid-cols-4">
          <FormField
            label="Customer"
            required
            control={
              <Combobox
                value={customerId}
                onChange={v => setCustomerId(v)}
                options={customerOptions}
                placeholder="Select customer…"
              />
            }
          />
          <FormField label="Issue date" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
          <FormField label="Due date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          <FormField
            label="Currency"
            control={
              <CurrencyCombobox
                value={currency}
                onChange={v => setCurrency(v)}
              />
            }
          />
          <FormField
            label="PO number"
            value={poNumber}
            onChange={e => { setPoNumber(e.target.value); setPoAutoFilled(false) }}
            placeholder={customerId ? 'Allocated from customer sequence' : 'Select a customer first'}
            hint="Auto-filled per customer - editable"
            containerClassName="md:col-span-2"
          />
        </div>
      </Card>

      <Card>
        <div className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Line items</h3>
            <Button size="sm" variant="secondary" onClick={() => setLines(prev => [...prev, blankLine()])}>
              <Plus className="mr-1 size-4" /> Add line
            </Button>
          </div>
          <div className="space-y-3">
            {lines.map((line, idx) => {
              const amount = Number(line.quantity || 0) * Number(line.unit_price || 0)
              return (
                <div key={idx} className="grid gap-3 rounded-md border bg-card p-3 md:grid-cols-12">
                  <FormField
                    label="#"
                    containerClassName="md:col-span-1"
                    value={String(idx + 1)}
                    disabled
                  />
                  <FormField
                    label="Description"
                    containerClassName="md:col-span-3"
                    value={line.description}
                    onChange={e => updateLine(idx, { description: e.target.value })}
                  />
                  <FormField
                    label="Revenue account"
                    containerClassName="md:col-span-3"
                    control={
                      <Combobox
                        value={line.account_code || null}
                        onChange={v => updateLine(idx, { account_code: v ? String(v) : '' })}
                        options={revenueAccountOptions}
                        placeholder="Pick account…"
                        emptyMessage="No revenue accounts in your COA."
                      />
                    }
                  />
                  <FormField
                    label="Unit price"
                    type="number"
                    step="0.01"
                    containerClassName="md:col-span-1"
                    value={line.unit_price}
                    onChange={e => updateLine(idx, { unit_price: e.target.value })}
                  />
                  <FormField
                    label="Qty"
                    type="number"
                    containerClassName="md:col-span-1"
                    value={line.quantity}
                    onChange={e => updateLine(idx, { quantity: e.target.value })}
                  />
                  <FormField
                    label="Tax %"
                    type="number"
                    step="0.01"
                    containerClassName="md:col-span-1"
                    value={line.tax_rate}
                    onChange={e => updateLine(idx, { tax_rate: e.target.value })}
                  />
                  <div className="md:col-span-2 flex flex-col gap-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Amount</div>
                    <div className="flex h-10 items-center justify-between gap-0">
                      <span className="text-sm font-medium">{formatInvoiceMoney(amount, currency)}</span>
                      <div className="-mr-1 flex items-center">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-7"
                          onClick={() => moveLine(idx, -1)}
                          disabled={idx === 0}
                          aria-label="Move line up"
                          type="button"
                        >
                          <ArrowUp className="size-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-7"
                          onClick={() => moveLine(idx, 1)}
                          disabled={idx === lines.length - 1}
                          aria-label="Move line down"
                          type="button"
                        >
                          <ArrowDown className="size-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-7"
                          onClick={() => removeLine(idx)}
                          disabled={lines.length === 1}
                          aria-label="Remove line"
                          type="button"
                        >
                          <Trash2 className="size-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-4 flex flex-col items-end gap-2 text-sm">
            <div>Subtotal: <span className="font-medium">{formatInvoiceMoney(totals.subtotal, currency)}</span></div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                id="invoice-use-header-tax"
                checked={useHeaderTax}
                onCheckedChange={(v) => setUseHeaderTax(v === true)}
              />
              <label htmlFor="invoice-use-header-tax" className="cursor-pointer">
                Apply a single tax rate to the whole invoice (overrides per-line Tax %)
              </label>
            </div>
            {useHeaderTax ? (
              <div className="w-40">
                <FormField
                  label="Tax rate %"
                  type="number"
                  step="0.01"
                  min="0"
                  value={headerTaxRatePct}
                  onChange={e => setHeaderTaxRatePct(e.target.value)}
                  hint="e.g. 20 for 20%"
                />
              </div>
            ) : null}
            {totals.tax > 0 && (
              <div>
                Tax{useHeaderTax && headerTaxRatePct ? ` (${headerTaxRatePct}%)` : ''}:{' '}
                <span className="font-medium">{formatInvoiceMoney(totals.tax, currency)}</span>
              </div>
            )}
            <div className="w-40">
              <FormField
                label="Discount"
                type="number"
                step="0.01"
                min="0"
                value={discount}
                onChange={e => setDiscount(e.target.value)}
                hint="Flat amount subtracted (e.g. loyalty discount)"
              />
            </div>
            <div className="w-40">
              <FormField
                label="Shipping"
                type="number"
                step="0.01"
                min="0"
                value={shippingAmount}
                onChange={e => setShippingAmount(e.target.value)}
                hint="Optional flat charge added to the total"
              />
            </div>
            {Number(shippingAmount || 0) > 0 ? (
              <div className="w-72">
                <FormField
                  label="Shipping income account"
                  required
                  control={
                    <Combobox
                      value={shippingAccountCode || null}
                      onChange={v => setShippingAccountCode(v ? String(v) : '')}
                      options={revenueAccountOptions}
                      placeholder="Pick account…"
                      emptyMessage="No revenue accounts in your COA."
                    />
                  }
                  hint="Posted as income on the GL when this invoice is posted."
                />
              </div>
            ) : null}
            <div className="text-base">Total due: <span className="font-bold">{formatInvoiceMoney(totals.total, currency)}</span></div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-4">
          <div>
            <h3 className="text-sm font-semibold">Payment &amp; bank details</h3>
            <p className="text-xs text-muted-foreground">
              How the customer will pay this invoice. Defaults to your tenant payment-collection setting; override here if you want a single invoice to be bank-transfer only.
            </p>
          </div>

          {/* Payment-method picker. The "Use tenant default" option just
              passes the tenant's stripeConnect.mode through. Picking a
              method explicitly pins it on the invoice snapshot so a
              later tenant-level switch doesn't re-route this invoice. */}
          <FormField
            label="Payment collection method"
            control={
              <Combobox
                value={effectiveMethod ?? '__bank_only__'}
                onChange={(v) => {
                  if (v === '__inherit__') setPaymentMethodOverride(undefined)
                  else if (v === '__bank_only__') setPaymentMethodOverride(null)
                  else if (v === 'connect_express' || v === 'bokapsys_managed') {
                    setPaymentMethodOverride(v)
                  }
                }}
                options={[
                  ...(tenantDefaultMethod
                    ? [{
                        value: '__inherit__',
                        label: `Use tenant default (${tenantDefaultMethod === 'connect_express' ? 'Stripe Connect Express' : 'Bokapsys-managed'})`,
                      }]
                    : []),
                  { value: 'connect_express', label: 'Stripe Connect (Express)', description: 'Direct charge on your connected Stripe account' },
                  { value: 'bokapsys_managed', label: 'Bokapsys-managed', description: 'Bokapsys collects the charge and pays you out' },
                  { value: '__bank_only__', label: 'Bank transfer only (no online payment)', description: 'Hides the Pay button on the invoice link' },
                ]}
                placeholder="Pick a method…"
              />
            }
            hint={
              tenantDefaultMethod
                ? `Tenant default is ${tenantDefaultMethod === 'connect_express' ? 'Stripe Connect (Express)' : 'Bokapsys-managed'}. Change in Company → Payments.`
                : 'No tenant default set. Pick a method below or add bank details to allow bank-transfer payment only.'
            }
          />

          {effectiveMethod === 'connect_express' && !stripeReady && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-foreground" />
              <div className="flex-1">
                <div className="font-medium">Stripe Connect setup is not complete</div>
                <div className="text-muted-foreground">
                  Finish onboarding in Company → Payments, or switch this invoice to bank-transfer only and add bank details below.
                </div>
              </div>
              <Button asChild size="sm" variant="outline">
                <a href="/company?tab=payments">Finish setup</a>
              </Button>
            </div>
          )}

          {effectiveMethod === 'bokapsys_managed' && !payoutReady && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-foreground" />
              <div className="flex-1">
                <div className="font-medium">No payout bank account on file</div>
                <div className="text-muted-foreground">
                  Bokapsys uses your payout account to pay you out for invoices collected on this plan. Add it in Company → Bank Accounts.
                </div>
              </div>
              <Button asChild size="sm" variant="outline">
                <a href="/company?tab=bank-accounts">Add payout account</a>
              </Button>
            </div>
          )}

          {effectiveMethod === null && !bankFallbackReady && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-foreground" />
              <div className="flex-1">
                <div className="font-medium">No way for the customer to pay this invoice</div>
                <div className="text-muted-foreground">
                  Pick a payment method above, or add bank details below to let the customer pay by bank transfer.
                </div>
              </div>
            </div>
          )}

          {/* Bank details - moved here from the Branding section so it
              sits right next to the payment-method picker. By default
              the checkbox is on so PDFs include the saved tenant
              default; the user can uncheck if they want online payment
              only and don't want to show a bank block. */}
          <div className="flex items-start gap-3 border-t pt-4">
            <div className="pt-1">
              <Checkbox
                checked={showBankDetails}
                onCheckedChange={(v) => setShowBankDetails(v === true)}
                aria-label="Show bank details on invoice"
              />
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-xs font-medium">Bank details (optional - shown on invoice)</p>
              {bankAccounts.length > 0 ? (
                <FormField
                  label="Use saved bank account"
                  control={
                    <Combobox
                      value={selectedBankId}
                      onChange={(v) => onPickBank(v as number | null)}
                      options={bankAccounts.map((b) => ({
                        value: b.id,
                        label: b.label + (b.is_default ? ' (default)' : ''),
                        description: [b.bank_name, b.account_number].filter(Boolean).join(' · ') || undefined,
                      }))}
                      placeholder="None - enter custom details below"
                      emptyMessage="No saved bank accounts."
                    />
                  }
                  hint="Managed in Company → Bank Accounts."
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  No saved bank accounts yet. Add one in Company → Bank Accounts to auto-fill this section on new invoices.
                </p>
              )}
              <FormField
                label="Details shown on invoice"
                control={
                  <Textarea
                    rows={4}
                    value={bankDetails}
                    onChange={e => { setBankDetails(e.target.value); setSelectedBankId(null) }}
                    placeholder="Bank: ABC Bank&#10;Account Name: Your Company Ltd&#10;Account Number: 12345678"
                    disabled={!showBankDetails}
                  />
                }
              />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-4">
          <FormField
            label="Notes (shown on invoice)"
            control={
              <Textarea
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Thanks for your business!"
              />
            }
          />
          <FormField
            label="Payment terms (shown on invoice)"
            control={
              <Textarea
                rows={2}
                value={terms}
                onChange={e => setTerms(e.target.value)}
                placeholder="Net 30. Late payments may incur a 1.5% monthly fee."
              />
            }
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-4">
          <button
            type="button"
            onClick={() => setBrandingOpen((v) => !v)}
            className="flex w-full items-start gap-2 text-left"
            aria-expanded={brandingOpen}
            aria-controls="invoice-branding-body"
          >
            <ChevronRight
              className={`mt-0.5 size-4 shrink-0 transition-transform ${brandingOpen ? 'rotate-90' : ''}`}
              aria-hidden
            />
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Branding</h3>
              <p className="text-xs text-muted-foreground">
                Defaults to your Company settings. Click to {brandingOpen ? 'collapse' : 'expand'} and override the logo, business details, or bank info just for this invoice.
              </p>
            </div>
          </button>

          {brandingOpen ? (
            <div id="invoice-branding-body" className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="pt-1">
                  <Checkbox
                    checked={showLogo}
                    onCheckedChange={(v) => setShowLogo(v === true)}
                    aria-label="Show logo on invoice"
                  />
                </div>
                <div className="grid flex-1 gap-4 md:grid-cols-[160px_1fr]">
                  <div className="flex h-24 w-40 items-center justify-center rounded-md border bg-muted/40 p-2">
                    {displayedLogo ? (
                      <img
                        src={displayedLogo}
                        alt="Invoice logo preview"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">No logo</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium">Logo</p>
                    <p className="text-xs text-muted-foreground">
                      Appears on the PDF, payment page, and receipt email. Max 5 MB. PNG/SVG with a transparent background works best.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onLogoFile(e.currentTarget.files?.[0])}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo || !showLogo}
                      >
                        {uploadingLogo ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
                        {senderLogoKey ? 'Replace invoice logo' : 'Upload invoice logo'}
                      </Button>
                      {senderLogoKey ? (
                        <Button type="button" variant="ghost" onClick={clearLogoOverride}>
                          Use company logo
                        </Button>
                      ) : null}
                    </div>
                    {!tenantLogoUrl && !senderLogoKey ? (
                      <p className="text-xs text-warning-foreground">
                        No company logo configured. Upload one in Company settings to use it by default.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex items-start gap-3">
                  <div className="pt-8">
                    <Checkbox
                      checked={showBusinessName}
                      onCheckedChange={(v) => setShowBusinessName(v === true)}
                      aria-label="Show business name on invoice"
                    />
                  </div>
                  <FormField
                    label="Business name"
                    value={senderBusinessName}
                    onChange={e => setSenderBusinessName(e.target.value)}
                    placeholder={tenantSettings?.tenant?.business_name ?? 'Your company'}
                    disabled={!showBusinessName}
                    containerClassName="flex-1"
                  />
                </div>
                <div className="flex items-start gap-3">
                  <div className="pt-8">
                    <Checkbox
                      checked={showBusinessPhone}
                      onCheckedChange={(v) => setShowBusinessPhone(v === true)}
                      aria-label="Show business phone on invoice"
                    />
                  </div>
                  <FormField
                    label="Business phone"
                    value={senderBusinessPhone}
                    onChange={e => setSenderBusinessPhone(e.target.value)}
                    placeholder={tenantSettings?.tenant?.business_phone ?? ''}
                    disabled={!showBusinessPhone}
                    containerClassName="flex-1"
                  />
                </div>
                <div className="flex items-start gap-3">
                  <div className="pt-8">
                    <Checkbox
                      checked={showBusinessEmail}
                      onCheckedChange={(v) => setShowBusinessEmail(v === true)}
                      aria-label="Show business email on invoice"
                    />
                  </div>
                  <FormField
                    label="Business email"
                    value={senderBusinessEmail}
                    onChange={e => setSenderBusinessEmail(e.target.value)}
                    placeholder={tenantSettings?.tenant?.business_email ?? 'you@company.com'}
                    disabled={!showBusinessEmail}
                    containerClassName="flex-1"
                  />
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="pt-1">
                  <Checkbox
                    checked={showBusinessAddress}
                    onCheckedChange={(v) => setShowBusinessAddress(v === true)}
                    aria-label="Show business address on invoice"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <p className="text-xs font-medium">Business address</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <FormField
                      label="Address line 1"
                      value={senderAddressLine1}
                      onChange={e => setSenderAddressLine1(e.target.value)}
                      placeholder={tenantSettings?.tenant?.address_line1 ?? ''}
                      disabled={!showBusinessAddress}
                      containerClassName="md:col-span-2"
                    />
                    <FormField
                      label="Address line 2"
                      value={senderAddressLine2}
                      onChange={e => setSenderAddressLine2(e.target.value)}
                      placeholder={tenantSettings?.tenant?.address_line2 ?? ''}
                      disabled={!showBusinessAddress}
                      containerClassName="md:col-span-2"
                    />
                    <FormField
                      label="City"
                      value={senderCity}
                      onChange={e => setSenderCity(e.target.value)}
                      placeholder={tenantSettings?.tenant?.city ?? ''}
                      disabled={!showBusinessAddress}
                    />
                    <FormField
                      label="Country"
                      control={
                        <CountryCombobox
                          value={senderCountry || null}
                          onChange={v => setSenderCountry(v || '')}
                        />
                      }
                    />
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Bank details have moved to the Payment &amp; bank details section above.
              </p>
            </div>
          ) : null}
        </div>
      </Card>

      <Modal
        open={gateOpen}
        onOpenChange={setGateOpen}
        title={gateReason?.title || 'Cannot save invoice'}
        description={gateReason?.body}
        footer={
          <>
            <Button variant="secondary" onClick={() => setGateOpen(false)}>Stay on this invoice</Button>
            {gateReason?.cta ? (
              <Button asChild>
                <a href={gateReason.cta.href}>{gateReason.cta.label}</a>
              </Button>
            ) : null}
          </>
        }
      />
    </Page>
  )
}
